import React, { useState, useEffect } from 'react'
import Page from 'layout/access-management/page'
import PageTitle from 'layout/page-title'
import { split } from 'lodash'
import DescriptionCard from './description-card'
import Row from 'react-bootstrap/Row'
import Tabs from 'duties/shared/tabs'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'
import EmployeesTab from './tabs/employees'
import { toast } from 'react-toastify'
import RolesTab from './tabs/roles'
import { useConflict } from 'api/hooks/use-conflicts'
import { useUpdateMunicipalityAnalytic } from 'api/hooks/use-municipality-analytics'
import CenteredSpinner from 'shared/centered-spinner'
import { useQueryClient } from 'react-query'

function Conflict() {
  const path = window.location.pathname
  const conflictId = split(path, '/')[2]
  const [activeTab, setActiveTab] = useState('role')
  const [isEnabled, setIsEnabled] = useState(true)
  const queryClient = useQueryClient()

  const params = {
    id: conflictId
  }

  const { data: conflict, isLoading, isSuccess } = useConflict({ params })

  // Sync local state with server data when conflict loads
  useEffect(() => {
    if (isSuccess && conflict?.municipality_analytic) {
      setIsEnabled(conflict.municipality_analytic.enabled)
    }
  }, [isSuccess, conflict?.municipality_analytic])

  // Mutation to update municipality_analytic weight
  // Toggle uses weight: 0 (off) vs 2 (med) to enable/disable the conflict
  const { mutate: updateMunicipalityAnalytic, isLoading: isUpdating } =
    useUpdateMunicipalityAnalytic({
      options: {
        onSuccess: (_, variables) => {
          // Invalidate all conflict queries to refresh data on index and show pages
          queryClient.invalidateQueries({ queryKey: ['conflicts'] })
          const wasEnabled = variables.weight !== 'off'
          toast.success(
            wasEnabled
              ? 'Conflict enabled'
              : 'Conflict disabled for your organization',
            { position: 'top-center', autoClose: 2000 }
          )
        },
        onError: () => {
          // Revert local state on error
          setIsEnabled((prev) => !prev)
          toast.error('Failed to update conflict status', {
            position: 'top-center'
          })
        }
      }
    })

  const handleToggleConflict = () => {
    if (!conflict?.municipality_analytic?.id || isUpdating) return

    const newEnabled = !isEnabled
    setIsEnabled(newEnabled)

    // weight: 0 = off, 2 = med (default enabled weight)
    updateMunicipalityAnalytic({
      id: conflict.municipality_analytic.id,
      weight: newEnabled ? 'med' : 'off'
    })
  }

  const crumbs = [
    { name: 'Access Management', url: 'duties' },
    { name: 'Conflicts', url: 'conflicts' }
  ]

  return (
    <Page
      title={`${isSuccess ? conflict.name : ''}`}
      pageTitle={`Conflict - ${isSuccess ? conflict.name : ''}`}
    >
      <PageTitle breadcrumbs={crumbs} />
      {isLoading && <CenteredSpinner />}

      {isSuccess && (
        <>
          <div className="d-flex justify-content-between align-items-center mt-4">
            <h4 className="mb-0">{conflict.name}</h4>
            {/* Toggle to enable/disable this conflict for the municipality */}
            {/* Uses weight: 0 (off) vs 2 (med) on municipality_analytics */}
            {conflict.municipality_analytic?.id && (
              <div className="d-flex align-items-center">
                <span className="me-2 text-muted">
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="conflictEnabledSwitch"
                    checked={isEnabled}
                    disabled={isUpdating}
                    onChange={handleToggleConflict}
                    style={{ width: '3em', height: '1.5em', cursor: 'pointer' }}
                  />
                </div>
              </div>
            )}
          </div>
          <hr />
          {!isEnabled && (
            <Alert variant="warning" className="mb-3">
              This conflict is disabled and will not appear elsewhere in the
              application or be included in any counts.
            </Alert>
          )}
          <Row>
            <Col sm={12}>
              <div className="mb-3">
                <DescriptionCard conflict={conflict} params={params} />
              </div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col sm={12}>
              <div className="d-flex mb-3">
                <Tabs
                  tabs={[
                    { name: 'Roles', value: 'role' },
                    { name: 'Users', value: 'employee' }
                  ]}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                />
              </div>
            </Col>

            <Col sm={12}>
              <Card>
                <Card.Body>
                  {activeTab === 'role' && <RolesTab conflict={conflict} />}
                  {activeTab === 'employee' && (
                    <EmployeesTab conflict={conflict} />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Page>
  )
}

export default Conflict
